import { useEffect, useMemo, useState } from "react";
import { AlertTriangle, Wrench } from "lucide-react";
import { Seo } from "@/components/Seo";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { FeedbackMessage } from "@/components/ui/feedback";
import { Input, Label, Textarea } from "@/components/ui/input";
import { DemoBanner } from "@/components/portal/DemoBanner";
import {
  assetNeedsAttention,
  listFleetAssets,
  updateFleetAsset,
  type FleetAssetRecord,
  type FleetAssetStatus,
  type ServiceUrgency,
} from "@/features/fleet/repo";

const statusVariant: Record<FleetAssetStatus, "success" | "warning" | "danger"> = {
  active: "success",
  maintenance: "warning",
  out_of_service: "danger",
};

const statusLabel: Record<FleetAssetStatus, string> = {
  active: "Active",
  maintenance: "Needs attention",
  out_of_service: "Out of service",
};

const serviceLabel: Record<ServiceUrgency, string> = {
  none: "No reminder",
  ok: "OK",
  due_soon: "Due soon",
  overdue: "Overdue",
};

const serviceVariant: Record<ServiceUrgency, "default" | "warning" | "danger" | "success"> = {
  none: "default",
  ok: "success",
  due_soon: "warning",
  overdue: "danger",
};

export default function InternalFleet() {
  const [assets, setAssets] = useState<FleetAssetRecord[]>([]);
  const [selected, setSelected] = useState<FleetAssetRecord | null>(null);
  const [statusFilter, setStatusFilter] = useState<"all" | FleetAssetStatus>("all");
  const [groupFilter, setGroupFilter] = useState<string>("all");
  const [unitFilter, setUnitFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function refresh() {
    setLoading(true);
    try {
      const data = await listFleetAssets();
      setAssets(data);
      setSelected((current) =>
        current ? data.find((a) => a.id === current.id) ?? data[0] ?? null : data[0] ?? null,
      );
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not load fleet assets.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void refresh();
  }, []);

  const groups = useMemo(() => {
    const values = new Set<string>();
    for (const asset of assets) {
      if (asset.fleetGroup) values.add(asset.fleetGroup);
    }
    return [...values].sort((a, b) => a.localeCompare(b));
  }, [assets]);

  const filtered = useMemo(() => {
    const unitNeedle = unitFilter.trim().toLowerCase();
    return assets.filter((asset) => {
      if (statusFilter !== "all" && asset.status !== statusFilter) return false;
      if (groupFilter !== "all" && asset.fleetGroup !== groupFilter) return false;
      if (unitNeedle && !asset.unit.toLowerCase().includes(unitNeedle)) return false;
      return true;
    });
  }, [assets, groupFilter, statusFilter, unitFilter]);

  const counts = useMemo(
    () => ({
      active: assets.filter((a) => a.status === "active").length,
      maintenance: assets.filter((a) => a.status === "maintenance").length,
      outOfService: assets.filter((a) => a.status === "out_of_service").length,
      overdue: assets.filter((a) => a.serviceUrgency === "overdue").length,
      dueSoon: assets.filter((a) => a.serviceUrgency === "due_soon").length,
    }),
    [assets],
  );

  async function saveNotes(asset: FleetAssetRecord, notes: string) {
    setSaving(true);
    setError(null);
    try {
      await updateFleetAsset(asset, { notes });
      await refresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not save notes.");
    } finally {
      setSaving(false);
    }
  }

  async function savePreviewStatus(asset: FleetAssetRecord, status: FleetAssetStatus) {
    if (asset.fleetioId) return;
    setSaving(true);
    setError(null);
    try {
      await updateFleetAsset(asset, { status });
      await refresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not update status.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <>
      <Seo title="Fleet" noindex />
      <DemoBanner detail="Hard-refresh this page if Group/Driver columns still look empty." />

      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold">Fleet & maintenance</h1>
          <p className="mt-1 max-w-3xl text-sm text-brand-500">
            Live equipment from Fleetio: unit, fleet group, assigned driver, odometer,
            service urgency, and open issues/work orders. Staff notes are the only field
            we store locally.
          </p>
        </div>
        <div className="grid grid-cols-2 gap-2 text-center text-xs sm:grid-cols-5">
          <Stat label="Active" value={counts.active} />
          <Stat label="Needs attention" value={counts.maintenance} />
          <Stat label="Out of service" value={counts.outOfService} />
          <Stat label="Service overdue" value={counts.overdue} />
          <Stat label="Due soon" value={counts.dueSoon} />
        </div>
      </div>

      {error && (
        <FeedbackMessage tone="error" className="mt-4">
          {error}
        </FeedbackMessage>
      )}

      <div className="mt-4 grid gap-3 sm:grid-cols-3 lg:max-w-3xl">
        <div>
          <Label htmlFor="fleet-unit-filter">Unit number</Label>
          <Input
            id="fleet-unit-filter"
            value={unitFilter}
            onChange={(e) => setUnitFilter(e.target.value)}
            placeholder="Search unit, e.g. 101"
            className="mt-1"
          />
        </div>
        <div>
          <Label htmlFor="fleet-status-filter">Filter status</Label>
          <select
            id="fleet-status-filter"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as "all" | FleetAssetStatus)}
            className="mt-1 flex h-10 w-full rounded-md border border-brand-200 bg-white px-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-500"
          >
            <option value="all">All assets ({assets.length})</option>
            <option value="active">Active ({counts.active})</option>
            <option value="maintenance">Needs attention ({counts.maintenance})</option>
            <option value="out_of_service">Out of service ({counts.outOfService})</option>
          </select>
        </div>
        <div>
          <Label htmlFor="fleet-group-filter">Filter group</Label>
          <select
            id="fleet-group-filter"
            value={groupFilter}
            onChange={(e) => setGroupFilter(e.target.value)}
            className="mt-1 flex h-10 w-full rounded-md border border-brand-200 bg-white px-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-500"
          >
            <option value="all">All groups</option>
            {groups.map((group) => (
              <option key={group} value={group}>
                {group}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="mt-6 grid gap-6 lg:grid-cols-5">
        <div className="grid gap-3 md:hidden">
          {loading && (
            <p className="rounded-lg border border-brand-100 bg-white p-4 text-sm text-brand-500">
              Loading fleet...
            </p>
          )}
          {!loading && filtered.length === 0 && (
            <p className="rounded-lg border border-dashed border-brand-200 bg-white p-4 text-sm text-brand-500">
              No fleet assets match these filters. Try another unit, status, or group.
            </p>
          )}
          {!loading &&
            filtered.map((asset) => (
              <button
                key={asset.id}
                type="button"
                onClick={() => setSelected(asset)}
                className={`rounded-xl border bg-white p-4 text-left shadow-sm transition ${
                  selected?.id === asset.id
                    ? "border-accent-500 ring-2 ring-accent-500/10"
                    : "border-brand-100 hover:border-brand-200"
                } ${assetNeedsAttention(asset) ? "border-l-4 border-l-amber-400" : ""}`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="font-medium text-brand-900">Unit {asset.unit}</p>
                    <p className="mt-1 break-words text-sm text-brand-600">
                      {asset.vehicleType || asset.assetType}
                    </p>
                    {asset.fleetGroup && (
                      <p className="mt-0.5 text-xs text-brand-500">{asset.fleetGroup}</p>
                    )}
                  </div>
                  <Badge variant={statusVariant[asset.status]}>{statusLabel[asset.status]}</Badge>
                </div>
                <div className="mt-3 flex flex-wrap items-center gap-2 text-xs text-brand-500">
                  <Badge variant={serviceVariant[asset.serviceUrgency]}>
                    {serviceLabel[asset.serviceUrgency]}
                  </Badge>
                  <span>{asset.assignedDriver || "No assigned driver"}</span>
                  {asset.nextServiceDue && <span>Due {asset.nextServiceDue}</span>}
                </div>
              </button>
            ))}
        </div>

        <div className="hidden overflow-x-auto rounded-lg border border-brand-100 md:block lg:col-span-3">
          <table className="w-full text-left text-sm">
            <thead className="bg-brand-50 text-brand-600">
              <tr>
                <th className="px-4 py-2">Unit</th>
                <th className="px-4 py-2">Group</th>
                <th className="px-4 py-2">Equipment</th>
                <th className="px-4 py-2">Driver</th>
                <th className="px-4 py-2">Service</th>
                <th className="px-4 py-2">Status</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td colSpan={6} className="px-4 py-6 text-center text-brand-500">
                    Loading fleet...
                  </td>
                </tr>
              )}
              {!loading && filtered.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-4 py-6 text-center text-brand-500">
                    No fleet assets match these filters. Try another unit, status, or group.
                  </td>
                </tr>
              )}
              {!loading &&
                filtered.map((asset) => (
                  <tr
                    key={asset.id}
                    className={`cursor-pointer border-t border-brand-100 hover:bg-brand-50/80 ${
                      selected?.id === asset.id ? "bg-accent-500/5" : ""
                    } ${assetNeedsAttention(asset) ? "border-l-4 border-l-amber-400" : ""}`}
                    onClick={() => setSelected(asset)}
                  >
                    <td className="px-4 py-2 font-medium">{asset.unit}</td>
                    <td className="px-4 py-2">{asset.fleetGroup || "-"}</td>
                    <td className="px-4 py-2">
                      <div>{asset.vehicleType || asset.assetType}</div>
                      {asset.equipment && asset.equipment !== asset.vehicleType && (
                        <div className="text-xs text-brand-500">{asset.equipment}</div>
                      )}
                    </td>
                    <td className="px-4 py-2">{asset.assignedDriver || "-"}</td>
                    <td className="px-4 py-2">
                      <div className="flex flex-col gap-1">
                        <Badge variant={serviceVariant[asset.serviceUrgency]}>
                          {serviceLabel[asset.serviceUrgency]}
                        </Badge>
                        {asset.nextServiceDue && (
                          <span className="text-xs text-brand-500">{asset.nextServiceDue}</span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-2">
                      <Badge variant={statusVariant[asset.status]}>{statusLabel[asset.status]}</Badge>
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>

        <Card className="min-w-0 lg:col-span-2">
          <CardHeader>
            <CardTitle>{selected ? `Unit ${selected.unit}` : "Select an asset"}</CardTitle>
          </CardHeader>
          <CardContent>
            {!selected && (
              <p className="text-sm text-brand-500">Choose a row to review Fleetio details and add staff notes.</p>
            )}
            {selected && (
              <div className="space-y-4">
                <DetailRow label="Fleet group" value={selected.fleetGroup || "-"} />
                <DetailRow label="Equipment" value={selected.equipment || selected.assetType} />
                <DetailRow label="Assigned driver" value={selected.assignedDriver || "-"} />
                <DetailRow label="Odometer" value={selected.odometer || "-"} />
                <DetailRow label="Fleetio status" value={selected.fleetioStatus || statusLabel[selected.status]} />
                <DetailRow label="Ownership" value={selected.ownership || "-"} />
                <DetailRow label="VIN" value={selected.vin || "-"} />

                {(selected.openIssues > 0 || selected.openWorkOrders > 0) && (
                  <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
                    <div className="flex items-center gap-2 font-medium">
                      <AlertTriangle className="h-4 w-4" />
                      Open maintenance items
                    </div>
                    <p className="mt-1">
                      {selected.openIssues > 0 ? `${selected.openIssues} issue(s)` : null}
                      {selected.openIssues > 0 && selected.openWorkOrders > 0 ? " · " : null}
                      {selected.openWorkOrders > 0 ? `${selected.openWorkOrders} work order(s)` : null}
                    </p>
                  </div>
                )}

                <div className="rounded-md border border-brand-100 bg-brand-50/60 p-3">
                  <div className="flex items-center gap-2 text-sm font-medium text-brand-800">
                    <Wrench className="h-4 w-4" />
                    Service reminder
                  </div>
                  <p className="mt-2 text-sm text-brand-700">
                    {selected.nextServiceTask || "No scheduled task"}
                  </p>
                  <div className="mt-2 flex items-center gap-2">
                    <Badge variant={serviceVariant[selected.serviceUrgency]}>
                      {serviceLabel[selected.serviceUrgency]}
                    </Badge>
                    {selected.nextServiceDue && (
                      <span className="text-sm text-brand-600">Due {selected.nextServiceDue}</span>
                    )}
                  </div>
                </div>

                {!selected.fleetioId && (
                  <div>
                    <Label htmlFor="fleet-status">Preview status override</Label>
                    <select
                      id="fleet-status"
                      className="mt-1 flex h-10 w-full rounded-md border border-brand-200 bg-white px-3 text-sm"
                      value={selected.status}
                      disabled={saving}
                      onChange={(e) =>
                        void savePreviewStatus(selected, e.target.value as FleetAssetStatus)
                      }
                    >
                      {(Object.keys(statusLabel) as FleetAssetStatus[]).map((status) => (
                        <option key={status} value={status}>
                          {statusLabel[status]}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                <div>
                  <Label htmlFor="fleet-notes">Staff notes</Label>
                  <Textarea
                    id="fleet-notes"
                    className="mt-1"
                    defaultValue={selected.notes}
                    disabled={saving}
                    onBlur={(e) => {
                      if (e.target.value !== selected.notes) {
                        void saveNotes(selected, e.target.value);
                      }
                    }}
                  />
                  {selected.fleetioId && (
                    <p className="mt-1 text-xs text-brand-400">
                      Fleetio fields sync from the API. Only staff notes are saved in our system.
                    </p>
                  )}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </>
  );
}

function DetailRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-start justify-between gap-4 text-sm">
      <span className="text-brand-500">{label}</span>
      <span className="min-w-0 break-words text-right font-medium text-brand-800">{value}</span>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: number }) {
  return (
    <div className="rounded-lg border border-brand-100 bg-white px-3 py-2">
      <div className="text-lg font-bold text-brand-800">{value}</div>
      <div className="text-brand-500">{label}</div>
    </div>
  );
}
